// ---------------------------------------------------------------------------
// The small pieces every admin panel shares.
//
// Split out of `AdminScreen.tsx` when the screen passed a thousand lines. These
// are here because more than one panel needs them, not because they belong
// together by subject: `ConfirmAction` guards a destructive button in four
// panels, `clockTime` formats a timestamp in three, and `BanDraft` is filled in
// by the room detail and by the report queue but submitted by the ban form. A
// second copy of any of them would eventually disagree with the first.
//
// `AdminModeBadge` is the exception — one caller, the screen itself — and it is
// here rather than there because it is markup with no logic, which is what the
// rest of this file is.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { BanScope } from '@countdown/shared';
import { Button } from '../../components/primitives/index.js';

export interface BanDraft {
  subjectKind: 'user' | 'device' | 'ip';
  subject: string;
  scope: BanScope;
  roomCode: string;
  reason: string;
}

export const emptyBan: BanDraft = {
  subjectKind: 'ip',
  subject: '',
  scope: 'global',
  roomCode: '',
  reason: '',
};

export function bytes(value: number): string {
  const mb = value / (1024 * 1024);
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

export function uptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Fixed rather than inline, and in the accent colour, because its whole job is
 * to be impossible to miss while privileged data is on screen.
 */
export function AdminModeBadge() {
  return (
    <p
      data-admin-mode-badge=""
      className="label-mono fixed top-0 left-1/2 z-60 -translate-x-1/2 rounded-b-chip px-3 py-1.5"
      style={{ background: 'var(--c-accent)', color: 'var(--c-bg-alt)' }}
    >
      admin mode on
    </p>
  );
}

/**
 * Two clicks, and the second one is labelled with what it does rather than with
 * "OK". One mis-click must not empty the server.
 */
export function ConfirmAction({
  label,
  question,
  confirmLabel,
  onConfirm,
}: {
  label: string;
  question: string;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <Button variant="danger" onClick={() => setArmed(true)}>
        {label}
      </Button>
    );
  }

  return (
    <div
      role="group"
      aria-label={question}
      className="flex flex-wrap items-center gap-2 rounded-card border border-alert px-3 py-2"
    >
      <p className="text-sm text-ink">{question}</p>
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setArmed(false)}>
        Cancel
      </Button>
    </div>
  );
}
